import type { ApiError, Attachment } from "../../../shared/api.ts";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** A failed request. `status` 0 means the network failed before a response arrived. */
export class HttpError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.code = code;
  }
}

export type MockRequest = { method: HttpMethod; path: string; body: unknown };
export type MockHandler = (request: MockRequest) => Promise<unknown>;

export type UploadHandle = { promise: Promise<Attachment>; abort: () => void };
export type Uploader = (sessionId: string, file: File, onProgress: (fraction: number) => void) => UploadHandle;

/** The part of `EventSource` the turn stream uses, so a fake stream can stand in for it. */
export interface EventStream {
  addEventListener(type: string, listener: (event: MessageEvent<string>) => void): void;
  close(): void;
  onerror: ((event: Event) => void) | null;
  readonly readyState: number;
}
export type EventStreamFactory = (url: string) => EventStream;

let mockHandler: MockHandler | null = null;
let mockUploader: Uploader | null = null;
let mockStreams: EventStreamFactory | null = null;
let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

/** Installs the fixture layer used when `VITE_MOCK=1`. */
export function installMockTransport(transport: { handler: MockHandler; uploader: Uploader; streams: EventStreamFactory }): void {
  mockHandler = transport.handler;
  mockUploader = transport.uploader;
  mockStreams = transport.streams;
}

export function isMockTransport(): boolean {
  return mockHandler !== null;
}

/** `Me.csrfToken`, sent as `x-csrf-token` on every request that is not GET. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Called when a request answers 401, so the app can send the visitor to `/login`. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

type RequestOptions = {
  /** Return the 401 to the caller instead of redirecting (used by `GET /api/me`). */
  allowUnauthorized?: boolean;
  signal?: AbortSignal;
};

/**
 * One JSON request to the API: JSON in and out, same-origin cookies, the CSRF header on writes,
 * `ApiError` bodies thrown as `HttpError` with the server's message, and 401 sent to `/login`.
 */
export async function request<T>(method: HttpMethod, path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  if (mockHandler) {
    try {
      return (await mockHandler({ method, path, body })) as T;
    } catch (error) {
      if (error instanceof HttpError && error.status === 401 && !options.allowUnauthorized) {
        unauthorizedHandler?.();
      }
      throw error;
    }
  }

  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) {
    headers["content-type"] = "application/json";
  }
  if (method !== "GET" && csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new HttpError(0, "network");
  }

  if (response.status === 401) {
    if (!options.allowUnauthorized) {
      unauthorizedHandler?.();
    }
    const parsed = await readError(response);
    throw new HttpError(401, parsed.error, parsed.code);
  }
  if (!response.ok) {
    const parsed = await readError(response);
    throw new HttpError(response.status, parsed.error, parsed.code);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

async function readError(response: Response): Promise<ApiError> {
  try {
    const parsed = (await response.json()) as Partial<ApiError>;
    if (typeof parsed.error === "string" && parsed.error) {
      return { error: parsed.error, code: parsed.code };
    }
  } catch {
    // Not JSON: fall back to the status text.
  }
  return { error: response.statusText || `HTTP ${response.status}` };
}

/** `POST /api/uploads` (multipart `sessionId` and `file`) with upload progress. */
export function uploadFile(sessionId: string, file: File, onProgress: (fraction: number) => void): UploadHandle {
  if (mockUploader) {
    return mockUploader(sessionId, file, onProgress);
  }
  const xhr = new XMLHttpRequest();
  const promise = new Promise<Attachment>((resolve, reject) => {
    xhr.open("POST", "/api/uploads");
    xhr.setRequestHeader("accept", "application/json");
    if (csrfToken) {
      xhr.setRequestHeader("x-csrf-token", csrfToken);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status === 401) {
        unauthorizedHandler?.();
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as Attachment);
        } catch {
          reject(new HttpError(xhr.status, "Invalid response"));
        }
        return;
      }
      let message = xhr.statusText || `HTTP ${xhr.status}`;
      let code: string | undefined;
      try {
        const parsed = JSON.parse(xhr.responseText) as Partial<ApiError>;
        if (parsed.error) {
          message = parsed.error;
          code = parsed.code;
        }
      } catch {
        // Keep the status text.
      }
      reject(new HttpError(xhr.status, message, code));
    };
    xhr.onerror = () => reject(new HttpError(0, "network"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    const form = new FormData();
    form.append("sessionId", sessionId);
    form.append("file", file);
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}

/** Opens a server-sent event stream (`EventSource`, or the fake one in mock mode). */
export function openEventStream(url: string): EventStream {
  if (mockStreams) {
    return mockStreams(url);
  }
  return new EventSource(url, { withCredentials: true });
}
